/**
 * Micro-Break Coach Tool
 *
 * Pomodoro-style timer with guided break activities.
 */

import { StepFlow, CountdownTimer, formatTime, saveToolEntry, initButtonGroup, TimerChime, renderStressChart } from './tools-common';

export function initMicroBreak() {
    var root = document.getElementById('micro-break');
    if (!root) return;

    var flow = new StepFlow(root);
    var workTimer = null;
    var breakTimer = null;
    var chime = new TimerChime();
    var CIRCUMFERENCE = 2 * Math.PI * 90; // ~565.49

    var workDuration = 1500;
    var breakDuration = 300;
    var sessionLog = [];
    var currentActivity = '';
    var cycleCount = 0;

    function updateCycleLabel() {
        var label = root.querySelector('#break-cycle-label');
        if (label) label.textContent = 'Cycle ' + cycleCount;
        var labelBreak = root.querySelector('#break-cycle-label-break');
        if (labelBreak) labelBreak.textContent = 'Cycle ' + cycleCount;
    }

    function setPlayPauseIcon(btn, isPaused) {
        if (!btn) return;
        var playIcon = btn.querySelector('.tool-play-btn__icon--play');
        var pauseIcon = btn.querySelector('.tool-play-btn__icon--pause');
        if (playIcon && pauseIcon) {
            playIcon.style.display = isPaused ? '' : 'none';
            pauseIcon.style.display = isPaused ? 'none' : '';
        }
        btn.title = isPaused ? 'Resume' : 'Pause';
    }

    // Pattern buttons
    initButtonGroup(root, '.tool-pattern-btn', function (btn) {
        workDuration = parseInt(btn.dataset.work, 10);
        breakDuration = parseInt(btn.dataset['break'], 10);
    });

    function setRingProgress(ringId, remaining, total) {
        var ring = document.getElementById(ringId);
        if (!ring) return;
        var progress = remaining / total;
        ring.setAttribute('stroke-dashoffset', CIRCUMFERENCE * (1 - progress));
    }

    // Start working
    root.querySelector('.tool-start-btn').addEventListener('click', function () {
        startWork();
    });

    function startWork() {
        cycleCount++;
        flow.show('work');
        updateCycleLabel();

        var display = document.getElementById('break-work-timer');
        workTimer = new CountdownTimer(workDuration, function (remaining) {
            display.textContent = formatTime(remaining);
            setRingProgress('break-ring-progress', remaining, workDuration);
        }, function () {
            chime.play();
            flow.show('break-prompt');
        });
        workTimer.start();
        setPlayPauseIcon(document.getElementById('break-pause-btn'), false);
    }

    // Restart (work and break screens)
    root.querySelectorAll('.tool-app__restart-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            if (workTimer) workTimer.stop();
            if (breakTimer) breakTimer.stop();
            workTimer = null;
            breakTimer = null;
            flow.reset();
        });
    });

    // Pause/resume (work)
    var pauseBtn = document.getElementById('break-pause-btn');
    if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
            if (!workTimer) return;
            if (workTimer.paused) {
                workTimer.resume();
                setPlayPauseIcon(pauseBtn, false);
            } else {
                workTimer.pause();
                setPlayPauseIcon(pauseBtn, true);
            }
        });
    }

    // Activity selection
    var activityBtns = root.querySelectorAll('.tool-app__activity-btn');
    activityBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
            currentActivity = btn.dataset.activity;
            startBreak();
        });
    });

    // Skip break
    var skipBtn = document.getElementById('break-skip-btn');
    if (skipBtn) {
        skipBtn.addEventListener('click', function () {
            sessionLog.push({ cycle: cycleCount, break_taken: false, activity: null, stress: null });
            flow.show('rate');
        });
    }

    function startBreak() {
        flow.show('break-active');
        updateCycleLabel();
        // Label stays "SHORT BREAK" per Figma; activity name could be shown elsewhere if needed

        var display = document.getElementById('break-break-timer');
        breakTimer = new CountdownTimer(breakDuration, function (remaining) {
            display.textContent = formatTime(remaining);
            setRingProgress('break-break-ring', remaining, breakDuration);
        }, function () {
            chime.play();
            flow.show('rate');
        });
        breakTimer.start();
        setPlayPauseIcon(document.getElementById('break-pause-break-btn'), false);
    }

    // Skip break (on break-active screen)
    var skipBreakBtn = document.getElementById('break-skip-break-btn');
    if (skipBreakBtn) {
        skipBreakBtn.addEventListener('click', function () {
            if (breakTimer) breakTimer.stop();
            breakTimer = null;
            sessionLog.push({ cycle: cycleCount, break_taken: false, activity: null, stress: null });
            flow.show('rate');
        });
    }

    // Pause/resume (break)
    var pauseBreakBtn = document.getElementById('break-pause-break-btn');
    if (pauseBreakBtn) {
        pauseBreakBtn.addEventListener('click', function () {
            if (!breakTimer) return;
            if (breakTimer.paused) {
                breakTimer.resume();
                setPlayPauseIcon(pauseBreakBtn, false);
            } else {
                breakTimer.pause();
                setPlayPauseIcon(pauseBreakBtn, true);
            }
        });
    }

    // End break early
    var endBreakBtn = document.getElementById('break-end-break-btn');
    if (endBreakBtn) {
        endBreakBtn.addEventListener('click', function () {
            if (breakTimer) breakTimer.stop();
            breakTimer = null;
            flow.show('rate');
        });
    }

    // Rate stress and continue or end
    var rateSubmit = document.getElementById('break-rate-submit');
    if (rateSubmit) {
        rateSubmit.addEventListener('click', function () {
            var stress = parseInt(document.getElementById('break-stress-slider').value, 10);
            sessionLog.push({
                cycle: cycleCount,
                break_taken: !!currentActivity,
                activity: currentActivity || null,
                stress: stress,
            });
            currentActivity = '';
            startWork();
        });
    }

    // Stop session
    root.querySelectorAll('.tool-stop-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            if (workTimer) workTimer.stop();
            if (breakTimer) breakTimer.stop();
            endSession();
        });
    });

    function endSession() {
        var chartEl = document.getElementById('break-chart');
        renderStressChart(chartEl, sessionLog);

        var breaksTaken = sessionLog.filter(function (l) { return l.break_taken; }).length;
        var statCycles = document.getElementById('break-stat-cycles');
        if (statCycles) statCycles.textContent = cycleCount;
        var statBreaks = document.getElementById('break-stat-breaks');
        if (statBreaks) statBreaks.textContent = breaksTaken;

        var summaryEl = document.getElementById('break-summary');
        if (summaryEl) {
            var html = '<p><strong>Cycles completed:</strong> ' + cycleCount + '</p>';
            html += '<p><strong>Breaks taken:</strong> ' + breaksTaken + ' / ' + sessionLog.length + '</p>';
            if (sessionLog.length > 0) {
                var avgStress = sessionLog.reduce(function (sum, l) { return sum + (l.stress || 0); }, 0) / sessionLog.length;
                html += '<p><strong>Average stress:</strong> ' + avgStress.toFixed(1) + ' / 5</p>';
            }
            summaryEl.innerHTML = html;
        }

        var stressRatings = sessionLog.map(function (l) { return l.stress || 0; });

        saveToolEntry({
            tool_slug: 'micro-break',
            entry_type: 'session',
            data: {
                work_duration: workDuration,
                break_duration: breakDuration,
                cycles: cycleCount,
                log: sessionLog,
                stress_ratings: stressRatings,
            },
        });

        flow.show('complete');
    }

    // Restart
    root.querySelectorAll('.tool-restart-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            cycleCount = 0;
            sessionLog = [];
            currentActivity = '';
            flow.reset();
        });
    });
}
